import { GrowthOverviewMetric, type GrowthOverviewGranularity } from "../const";
import {
    DEFAULT_GROWTH_OVERVIEW_SORT,
    GROWTH_OVERVIEW_NAME_SORT_FIELD,
    GROWTH_OVERVIEW_SUMMARY_SORT_FIELD,
    type IGrowthOverviewSort,
    type IGrowthOverviewTableRow,
} from "../types";

import { isCategoryWeeklyUniqueUnavailable } from "./categoryWeeklyUniqueUtils";
import { getPeriodsForMetric, getSummaryForMetric } from "./mapApiRows";
import { normalizePop } from "./normalizePop";

const getNameSortLabel = (row: IGrowthOverviewTableRow): string => row.displayName;

const getEffectiveMetricForRow = (
    row: IGrowthOverviewTableRow,
    metric: GrowthOverviewMetric,
    granularity: GrowthOverviewGranularity,
): GrowthOverviewMetric =>
    isCategoryWeeklyUniqueUnavailable(granularity, metric, row.rowType) ? GrowthOverviewMetric.Visits : metric;

const compareNullableNumbers = (
    left: number | null,
    right: number | null,
    direction: IGrowthOverviewSort["direction"],
): number => {
    if (left == null && right == null) {
        return 0;
    }
    if (left == null) {
        return 1;
    }
    if (right == null) {
        return -1;
    }

    const delta = left - right;
    return direction === "asc" ? delta : -delta;
};

const compareStrings = (left: string, right: string, direction: IGrowthOverviewSort["direction"]): number => {
    const delta = left.localeCompare(right, undefined, { sensitivity: "base" });
    return direction === "asc" ? delta : -delta;
};

const getNumericSortValue = (
    row: IGrowthOverviewTableRow,
    field: string,
    metric: GrowthOverviewMetric,
    granularity: GrowthOverviewGranularity,
): number | null => {
    const effectiveMetric = getEffectiveMetricForRow(row, metric, granularity);

    if (field === GROWTH_OVERVIEW_SUMMARY_SORT_FIELD) {
        return getSummaryForMetric(row, effectiveMetric);
    }

    const period = getPeriodsForMetric(row, effectiveMetric).find((item) => item.key === field);
    return normalizePop(period?.pop ?? null);
};

const compareRows = (
    left: IGrowthOverviewTableRow,
    right: IGrowthOverviewTableRow,
    sort: IGrowthOverviewSort,
    metric: GrowthOverviewMetric,
    granularity: GrowthOverviewGranularity,
): number => {
    if (sort.field === GROWTH_OVERVIEW_NAME_SORT_FIELD) {
        return compareStrings(getNameSortLabel(left), getNameSortLabel(right), sort.direction);
    }

    return compareNullableNumbers(
        getNumericSortValue(left, sort.field, metric, granularity),
        getNumericSortValue(right, sort.field, metric, granularity),
        sort.direction,
    );
};

export const sortTableRows = (
    rows: IGrowthOverviewTableRow[],
    metric: GrowthOverviewMetric,
    granularity: GrowthOverviewGranularity,
    sort: IGrowthOverviewSort = DEFAULT_GROWTH_OVERVIEW_SORT,
): IGrowthOverviewTableRow[] =>
    [...rows]
        .sort((left, right) => compareRows(left, right, sort, metric, granularity))
        .map((row) => ({
            ...row,
            children: row.children ? sortTableRows(row.children, metric, granularity, sort) : undefined,
        }));

export const getNextGrowthOverviewSort = (current: IGrowthOverviewSort, field: string): IGrowthOverviewSort => {
    if (current.field === field) {
        return {
            field,
            direction: current.direction === "asc" ? "desc" : "asc",
        };
    }

    return {
        field,
        direction: field === GROWTH_OVERVIEW_NAME_SORT_FIELD ? "asc" : "desc",
    };
};
